import { useEffect, useState } from "react";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Robot, Plus, Trash, Copy, Warning, CheckCircle, Info, Terminal, Key } from "@phosphor-icons/react";

const API_ORIGIN = BACKEND_URL;
const MCP_ENDPOINT = `${API_ORIGIN}/api/mcp`;

/**
 * Admin panel : Personal Access Tokens for the MCP server.
 * The full token value is shown ONCE at creation and never again — we only
 * store a bcrypt hash, so admins must copy immediately.
 */
export default function MCPPanel() {
  const [tokens, setTokens] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null); // shown once after creation
  const [form, setForm] = useState({ name: "", scopes: ["read", "write"] });

  const load = async () => {
    try {
      const r = await api.get("/admin/mcp/tokens");
      setTokens(r.data.tokens || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const createToken = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Donne un nom au token (Claude Bureau, iPhone…)");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/admin/mcp/tokens", { name: form.name.trim(), scopes: form.scopes });
      setNewToken(r.data);
      setForm({ name: "", scopes: ["read", "write"] });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Révoquer le token « ${t.name} » ? Les clients qui l'utilisent perdront l'accès immédiatement.`)) return;
    try {
      await api.delete(`/admin/mcp/tokens/${t.id}`);
      toast.success("Révoqué");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  return (
    <div className="space-y-4" data-testid="mcp-panel">
      <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/12 via-[#005CFF]/5 to-transparent border border-[#005CFF]/25 p-4 flex gap-3 items-start">
        <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)] shrink-0">
          <Robot size={22} weight="fill" color="white" />
        </div>
        <div className="text-sm text-white/85 leading-relaxed">
          <b className="text-white">Contrôle BKflow par la voix depuis Claude Desktop.</b>
          <div className="text-xs text-white/60 mt-1">
            Une fois configuré, tu diras à Claude : <i>« Planifie une intervention chez Dupont demain 10h avec Mher »</i> ou <i>« Combien de tickets SAV ouverts ? »</i> et il exécutera directement dans BKflow via ce serveur MCP.
          </div>
        </div>
      </div>

      {/* Config snippet */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5">
          <Terminal size={12} weight="duotone" /> Configuration Claude Desktop
        </div>
        <div className="text-xs text-white/70 leading-relaxed">
          Ouvre <code className="text-[10px]">~/Library/Application Support/Claude/claude_desktop_config.json</code>
          {" "}et ajoute-y :
        </div>
        <ConfigSnippet endpoint={MCP_ENDPOINT} />
        <div className="text-[11px] text-white/50 flex gap-2 items-start pt-1">
          <Info size={12} weight="duotone" className="shrink-0 mt-0.5 text-[#7CB2FF]" />
          <div>
            Remplace <code className="text-[10px]">&lt;TON_TOKEN&gt;</code> par celui que tu génères ci-dessous. Redémarre Claude Desktop après modification.
          </div>
        </div>
      </div>

      {/* Existing tokens */}
      <div className="space-y-2">
        {tokens.length === 0 && (
          <div className="rounded-xl bg-white/[0.02] border border-dashed border-white/10 p-6 text-center text-sm text-white/40">
            Aucun token émis. Génère-en un pour commencer.
          </div>
        )}
        {tokens.map((t) => (
          <div key={t.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3" data-testid={`mcp-token-${t.id}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${t.revoked_at ? "bg-white/5 border border-white/10" : "bg-emerald-500/15 border border-emerald-500/30"}`}>
              <Key size={15} weight="fill" className={t.revoked_at ? "text-white/30" : "text-emerald-400"} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate flex items-center gap-2">
                {t.name}
                {t.revoked_at && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300">Révoqué</span>}
              </div>
              <div className="text-[10px] text-white/40 font-mono">{t.prefix}… · scopes: {(t.scopes || []).join(", ")}</div>
              <div className="text-[9.5px] text-white/40">
                Créé {t.created_at?.slice(0, 10)}
                {t.last_used_at && <> · Dernier appel {new Date(t.last_used_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</>}
              </div>
            </div>
            {!t.revoked_at && (
              <button onClick={() => revoke(t)} className="text-white/40 hover:text-red-400 p-1.5" title="Révoquer" data-testid={`mcp-revoke-${t.id}`}>
                <Trash size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* New token form */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-white/60">Nouveau token</div>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Ex : Claude Bureau Mac, iPhone Siri…"
          className="bg-white/5 border-white/10"
          data-testid="mcp-token-name"
        />
        <div className="flex items-center gap-2">
          {["read", "write"].map((s) => {
            const on = form.scopes.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => setForm({ ...form, scopes: on ? form.scopes.filter((x) => x !== s) : [...form.scopes, s] })}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 text-white/60"}`}
                data-testid={`mcp-scope-${s}`}
              >
                {s === "read" ? "Lecture" : "Écriture (créer chantier/intervention/SAV)"}
              </button>
            );
          })}
        </div>
        <Button onClick={createToken} disabled={creating} className="w-full bg-[#005CFF] hover:bg-[#0047CC]" data-testid="mcp-create-token">
          <Plus size={13} weight="bold" /> {creating ? "Création…" : "Générer un token"}
        </Button>
      </div>

      <NewTokenDialog token={newToken} onClose={() => setNewToken(null)} endpoint={MCP_ENDPOINT} onCopy={copy} />
    </div>
  );
}

function ConfigSnippet({ endpoint }) {
  const json = `{
  "mcpServers": {
    "bkflow": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-remote",
        "${endpoint}",
        "--header",
        "Authorization: Bearer <TON_TOKEN>"
      ]
    }
  }
}`;
  return (
    <div className="relative">
      <pre className="bg-black/50 rounded-lg p-3 text-[10.5px] leading-relaxed overflow-x-auto text-white/85 font-mono">{json}</pre>
      <button
        onClick={() => { navigator.clipboard.writeText(json); toast.success("Config copiée"); }}
        className="absolute top-2 right-2 text-white/40 hover:text-[#7CB2FF] p-1.5 bg-black/40 rounded"
        title="Copier"
        data-testid="mcp-copy-config"
      >
        <Copy size={12} />
      </button>
    </div>
  );
}

function NewTokenDialog({ token, onClose, endpoint, onCopy }) {
  if (!token) return null;
  return (
    <Dialog open={!!token} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#111] border-white/10 text-white rounded-2xl" data-testid="mcp-new-token-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CheckCircle size={20} weight="duotone" className="text-emerald-400" /> Token créé
          </DialogTitle>
          <DialogDescription className="text-xs text-white/60">
            Copie le token maintenant — il ne sera <b>plus jamais</b> visible ensuite. Nous n&apos;en stockons qu&apos;un hash bcrypt.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 flex gap-2 items-start text-xs text-amber-200">
            <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
            <div>Si tu perds ce token, tu devras en générer un nouveau.</div>
          </div>
          <div className="rounded-xl bg-black/40 border border-white/10 p-3 space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Token</div>
            <code className="block font-mono text-[11px] break-all text-emerald-300 select-all">{token.token}</code>
            <Button onClick={() => onCopy(token.token)} size="sm" className="w-full bg-[#005CFF] hover:bg-[#0047CC] mt-1">
              <Copy size={12} /> Copier le token
            </Button>
          </div>
          <details className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-xs">
            <summary className="cursor-pointer text-white/70">Voir la config Claude Desktop pré-remplie</summary>
            <pre className="mt-2 bg-black/40 rounded p-2 text-[10px] font-mono overflow-x-auto text-white/85">{`{
  "mcpServers": {
    "bkflow": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${endpoint}", "--header", "Authorization: Bearer ${token.token}"]
    }
  }
}`}</pre>
            <Button onClick={() => onCopy(`{
  "mcpServers": {
    "bkflow": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${endpoint}", "--header", "Authorization: Bearer ${token.token}"]
    }
  }
}`)} size="sm" className="w-full mt-2 bg-white/10 hover:bg-white/15">
              <Copy size={12} /> Copier la config complète
            </Button>
          </details>
          <Button onClick={onClose} className="w-full bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="mcp-new-token-done">
            J&apos;ai copié — Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
